import React, { useEffect, useState } from "react";
import "./CubeSatTelemetry.css";

const API = "/api";

const randomWalk = (base, variance = 5) =>
  base + (Math.random() - 0.5) * variance;

const createSeries = (base, count = 45, variance = 8) => {
  let value = base;

  return Array.from({ length: count }, () => {
    value += (Math.random() - 0.5) * variance;
    return value;
  });
};

function Gauge({ value, label }) {
  const angle = -135 + (value / 100) * 270;

  return (
    <div className="gauge-card">
      <div className="panel-title">{label}</div>

      <div className="gauge">
        <div
          className="gauge-needle"
          style={{ transform: `rotate(${angle}deg)` }}
        />

        <div className="gauge-center">
          <span>{Math.round(value)}%</span>
        </div>
      </div>

      <div className="gauge-labels">
        <span>0</span>
        <span>25</span>
        <span>50</span>
        <span>75</span>
        <span>100</span>
      </div>
    </div>
  );
}

function LineChart({
  title,
  series,
  min = 0,
  max = 100,
  unit = "",
}) {
  const width = 600;
  const height = 150;

  const points = series
    .map((value, index) => {
      const x =
        (index / (series.length - 1)) * width;

      const normalized =
        (value - min) / (max - min);

      const y =
        height -
        normalized * height;

      return `${x},${y}`;
    })
    .join(" ");

  return (
    <div className="chart-panel">
      <div className="chart-title">
        {title}
      </div>

      <div className="chart-wrapper">
        <svg
          viewBox={`0 0 ${width} ${height}`}
          preserveAspectRatio="none"
        >
          <g className="grid-lines">
            <line
              x1="0"
              y1="30"
              x2={width}
              y2="30"
            />

            <line
              x1="0"
              y1="75"
              x2={width}
              y2="75"
            />

            <line
              x1="0"
              y1="120"
              x2={width}
              y2="120"
            />
          </g>

          <polyline
            points={points}
            fill="none"
            className="chart-line"
          />
        </svg>

        <div className="chart-min">
          {min}{unit}
        </div>

        <div className="chart-max">
          {max}{unit}
        </div>
      </div>
    </div>
  );
}

function StatusCard({ title, value, className = "" }) {
  return (
    <div className={`status-card ${className}`}>
      <div className="status-title">
        {title}
      </div>

      <div className="status-value">
        {value}
      </div>
    </div>
  );
}

export default function CubeSatTelemetry() {
  const [telemetry, setTelemetry] =
    useState({
      battery1: 18,
      battery2: 20,
      battery3: 18,
      battery4: 20,
      temperature: 24,
      radioPA: 94,
      mode: "SAFE MODE!",
      reboots: 126,
    });

  const [powerSeries, setPowerSeries] =
    useState(createSeries(80, 45, 30));

  const [temperatureSeries, setTemperatureSeries] =
    useState(createSeries(25, 45, 15));

  const [radioSeries, setRadioSeries] =
    useState(createSeries(94, 45, 3));

  const [attitudeSeries, setAttitudeSeries] =
    useState(createSeries(0, 45, 5));

  useEffect(() => {
    const timer = setInterval(() => {
      setTelemetry((old) => ({
        ...old,

        battery1: Math.max(
          0,
          Math.min(
            100,
            randomWalk(old.battery1, 1)
          )
        ),

        battery2: Math.max(
          0,
          Math.min(
            100,
            randomWalk(old.battery2, 1)
          )
        ),

        battery3: Math.max(
          0,
          Math.min(
            100,
            randomWalk(old.battery3, 1)
          )
        ),

        battery4: Math.max(
          0,
          Math.min(
            100,
            randomWalk(old.battery4, 1)
          )
        ),

        temperature:
          randomWalk(old.temperature, 1),

        radioPA:
          randomWalk(old.radioPA, 2),
      }));

      setPowerSeries((old) => [
        ...old.slice(1),
        randomWalk(
          80 + Math.random() * 40,
          30
        ),
      ]);

      setTemperatureSeries((old) => [
        ...old.slice(1),
        randomWalk(25, 20),
      ]);

      setRadioSeries((old) => [
        ...old.slice(1),
        randomWalk(94, 4),
      ]);

      setAttitudeSeries((old) => [
        ...old.slice(1),
        randomWalk(0, 8),
      ]);
    }, 2000);

    return () => clearInterval(timer);
  }, []);

  /*
   * Later replace the simulated data above with:
   *
   * fetch(`${API}/cubesat/telemetry`)
   *
   * and map the Flask telemetry response here.
   */

  return (
    <div className="telemetry-app">

      {/* ==========================================
          TOP NAVIGATION
      =========================================== */}

      <header className="topbar">

        <div className="brand">
          <div className="brand-icon">
            ✦
          </div>

          <div className="brand-name">
            STEMX365
          </div>
        </div>

        <div className="dashboard-name">
          <span className="dashboard-icon">
            ▦
          </span>

          Telemetry /

          <strong>
            STEMX365-1
          </strong>

          <span className="arrow">
            ▾
          </span>
        </div>

        <div className="top-actions">

          <button>
            ◧
          </button>

          <span className="time">
            Aug 07, 2026 23:46:26
          </span>

          <span className="refresh">
            Refresh every 1m
          </span>

          <button>
            ⟳
          </button>

        </div>

      </header>


      {/* ==========================================
          MAIN
      =========================================== */}

      <main className="dashboard">

        {/* ========================================
            HEADER ACTIONS
        ========================================= */}

        <div className="dashboard-actions">

          <button>
            ↗ Ground Station
          </button>

          <button>
            ↗ Mission Database
          </button>

        </div>


        {/* ========================================
            FIRST ROW
        ========================================= */}

        <section className="top-grid">

          <div className="summary-panel">

            <div className="timestamp-title">
              Timestamp of most recent frame (UTC)
            </div>

            <div className="timestamp">
              08:07:27
            </div>

            <div className="spacecraft-name">
              FC Salt
              <strong>STEMX365-1</strong>
            </div>

            <div className="connection-grid">

              <StatusCard
                title="From"
                value="STEMXGS"
              />

              <StatusCard
                title="To"
                value="STEMX365"
              />

              <StatusCard
                title="Beacon Setting"
                value="14"
              />

              <StatusCard
                title="Reboots"
                value={telemetry.reboots}
              />

            </div>

          </div>


          {/* Battery gauges */}

          <Gauge
            label="BAT1 — EPS"
            value={telemetry.battery1}
          />

          <Gauge
            label="BAT2 — EPS"
            value={telemetry.battery2}
          />

          <Gauge
            label="BAT3 — EPS"
            value={telemetry.battery3}
          />

          <Gauge
            label="BAT4 — EPS"
            value={telemetry.battery4}
          />

        </section>


        {/* ========================================
            SECOND ROW
        ========================================= */}

        <section className="middle-grid">

          {/* Modes */}

          <div className="modes-panel">

            <div className="panel-title">
              Modes
            </div>

            <div className="mode-table">

              <div className="table-header">
                <span>Time</span>
                <span>Value</span>
              </div>

              {[
                "2026-08-07 22:42:51",
                "2026-08-07 22:44:30",
                "2026-08-07 22:45:04",
                "2026-08-07 22:45:37",
                "2026-08-07 22:46:10",
                "2026-08-07 22:46:43",
                "2026-08-07 22:47:16",
              ].map((time, index) => (
                <div
                  className="table-row"
                  key={time}
                >
                  <span>{time}</span>
                  <span>
                    {index === 6 ? "1.00" : "0.00"}
                  </span>
                </div>
              ))}

            </div>

            <div className="pagination">
              1&nbsp;&nbsp; 2&nbsp;&nbsp; 3&nbsp;&nbsp;
              4&nbsp;&nbsp; 5&nbsp;&nbsp; 6&nbsp;&nbsp; 7
            </div>

          </div>


          {/* Power chart */}

          <LineChart
            title="EPS Power / Battery Monitor"
            series={powerSeries}
            min={0}
            max={400}
            unit=" V"
          />

        </section>


        {/* ========================================
            THIRD ROW
        ========================================= */}

        <section className="grid-row">

          <StatusCard
            title="Mode of operation"
            value={telemetry.mode}
            className="large-status safe"
          />

          <StatusCard
            title="Status"
            value="NOMINAL"
            className="large-status"
          />

          <LineChart
            title="ACB PC Data — Attitude"
            series={attitudeSeries}
            min={-100}
            max={100}
          />

        </section>


        {/* ========================================
            FOURTH ROW
        ========================================= */}

        <section className="grid-row">

          <LineChart
            title="Radio PA Level"
            series={radioSeries}
            min={0}
            max={150}
          />

          <LineChart
            title="Battery Monitor Temperatures"
            series={temperatureSeries}
            min={-50}
            max={100}
            unit=" °C"
          />

        </section>


        {/* ========================================
            FIFTH ROW
        ========================================= */}

        <section className="grid-row">

          <LineChart
            title="CubeSat Temperatures"
            series={temperatureSeries}
            min={-50}
            max={100}
            unit=" °C"
          />

          <div className="event-panel">

            <div className="panel-title">
              Recent Events
            </div>

            <div className="event">
              <span>23:45:12</span>
              Telemetry received
            </div>

            <div className="event">
              <span>23:44:51</span>
              Battery status updated
            </div>

            <div className="event">
              <span>23:43:20</span>
              Attitude update
            </div>

            <div className="event">
              <span>23:42:08</span>
              Ground station pass
            </div>

          </div>

        </section>

      </main>


      {/* ==========================================
          SIDE NAV
      =========================================== */}

      <aside className="side-nav">

        <div className="side-item active">
          ▦
        </div>

        <div className="side-item">
          ◫
        </div>

        <div className="side-item">
          ◉
        </div>

        <div className="side-item">
          ⚙
        </div>

        <div className="side-bottom">
          ↪
        </div>

      </aside>

    </div>
  );
}